import { useMapGetter } from 'dashboard/composables/store';
import { formatTime } from '@chatwoot/utils';

/**
 * A composable function for report metrics calculations and display.
 *
 * @param {string} [accountSummaryKey='getAccountSummary'] - The key for accessing account summary data.
 * @returns {Object} An object containing utility functions for report metrics.
 */
export function useReportMetrics(
  accountSummaryKey = 'getAccountSummary',
  summarFetchingKey = 'getAccountSummaryFetchingStatus'
) {
  const accountSummary = useMapGetter(accountSummaryKey);
  const fetchingStatus = useMapGetter(summarFetchingKey);

  /**
   * Default set of metric keys mapped to their report field names.
   */
  const defaultMetrics = {
    CONVERSATIONS: 'conversations_count',
    OPEN_CONVERSATIONS: 'open_conversations_count',
    INCOMING_MESSAGES: 'incoming_messages_count',
    OUTGOING_MESSAGES: 'outgoing_messages_count',
    FIRST_RESPONSE_TIME: 'avg_first_response_time',
    RESOLUTION_TIME: 'avg_resolution_time',
    RESOLUTION_COUNT: 'resolutions_count',
    REPLY_TIME: 'reply_time',
  };

  /**
   * Metric keys specific to different entity types.
   */
  const metricsByType = {
    account: {},
    inbox: {
      CAMPAIGNS_CREATED: 'campaigns_created_count',
      CAMPAIGN_MESSAGES: 'campaign_messages_count',
    },
    agent: {},
    label: {},
    team: {},
  };

  /**
   * Returns the metrics based on the provided report type.
   *
   * @param {string} type - The report type.
   * @returns {Object} The combined metric keys.
   */
  const getReportMetrics = type => {
    return {
      ...defaultMetrics,
      ...(metricsByType[type] || {}),
    };
  };

  /**
   * Calculates the trend percentage for a given metric.
   *
   * @param {string} key - The key of the metric to calculate trend for.
   * @returns {number} The calculated trend percentage, rounded to the nearest integer.
   */
  const calculateTrend = key => {
    if (!accountSummary.value.previous[key]) return 0;
    const diff = accountSummary.value[key] - accountSummary.value.previous[key];
    return Math.round((diff / accountSummary.value.previous[key]) * 100);
  };

  /**
   * Checks if a given metric key represents an average metric type.
   *
   * @param {string} key - The key of the metric to check.
   * @returns {boolean} True if the metric is an average type, false otherwise.
   */
  const isAverageMetricType = key => {
    return [
      'avg_first_response_time',
      'avg_resolution_time',
      'reply_time',
    ].includes(key);
  };

  /**
   * Formats and displays a metric value based on its type.
   *
   * @param {string} key - The key of the metric to display.
   * @returns {string} The formatted metric value as a string.
   */
  const displayMetric = key => {
    if (isAverageMetricType(key)) {
      return formatTime(accountSummary.value[key]);
    }
    return Number(accountSummary.value[key] || '').toLocaleString();
  };

  return {
    defaultMetrics,
    metricsByType,
    getReportMetrics,
    calculateTrend,
    isAverageMetricType,
    displayMetric,
    fetchingStatus,
  };
}
